import React, { useState } from 'react';
import { getSourceConfig } from '../config';
import { useToast } from 'shared/toast';
// components
import { ActionsCell } from 'shared/grids/Cells';
import Button from 'shared/buttons';
import DocumentTypesSelectCell from '../DocumentTypesSelectCell';
import LoaderSpinner from 'shared/indicators/LoaderSpinner';
import { getDateStringForRequest } from 'src/shared/helpers/date';
import DataGrid from 'shared/grids/DataGrid';

const FilesToUploadGrid = ({
  source,
  sourceId,
  files,
  setFiles,
  handleReload,
  showDriverDoc,
  hideUploadAction,
  afterSave,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { postFiles } = getSourceConfig(source);

  const onCancel = ({ tempId }) => {
    const newData = files.filter((i) => i.tempId !== tempId);
    setFiles(newData);
  };

  const saveItems = (items) => postFiles(sourceId, items.map((i) => ({
    ...i,
    date: i.date instanceof Date ? getDateStringForRequest(i.date) : i.date,
  })))
    .then(() => {
      handleReload();
    })
    .catch((error) => {
      console.log('Failed to save File(s)', error);
      throw error;
    });

  const onSaveAll = () => {
    setIsSubmitting(true);
    saveItems(files)
      .then(() => {
        setFiles([]);
        toast.success({ message: 'Files uploaded successfully' });
        if (afterSave) {
          afterSave(files);
        }
      })
      .catch((error) => {
        toast.error({ message: 'Failed to upload Files', error });
      })
      .finally(() => setIsSubmitting(false));
  };

  const onSave = ({ tempId, ...dataItem }) => {
    setFiles((prev) => prev.map((i) => (i.tempId === tempId ? { ...i, isSubmitting: true } : i)));
    saveItems([dataItem])
      .then(() => {
        setFiles((prev) => prev.filter((i) => i.tempId !== tempId));
        toast.success({ message: 'File uploaded successfully' });
        if (afterSave) {
          afterSave([dataItem]);
        }
      })
      .catch((error) => {
        toast.error({ message: 'Failed to upload File', error });
      })
      .finally(() => {
        setFiles((prev) => prev.map((i) => (i.tempId === tempId ? { ...i, isSubmitting: false } : i)));
      });
  };

  const onItemChange = (event) => {
    const field = event.field || '';
    const newData = files
      .map((item) => (item.tempId === event.dataItem.tempId ? { ...item, [field]: event.value } : item));
    setFiles(newData);
  };

  function getColumns() {
    const driverDocColumn = { title: 'Driver Doc', field: 'driverDoc', editor: 'boolean' };

    const defaultColumns = [
      {
        title: 'Name',
        field: 'name',
        editable: false,
        width: 225,
      },
      {
        title: 'Document Type',
        field: 'documentTypeId',
        width: 225,
        cell: DocumentTypesSelectCell,
      },
      {
        title: 'Description',
        field: 'description',
        width: 200,
        editor: 'text',
      },
      {
        title: 'Date',
        field: 'date',
        format: '{0:d}',
        editor: 'date',
        editable: false,
        width: 100,
      },
      {
        title: 'Actions',
        cell: ({ dataItem, ...props }) => (
          <ActionsCell
            {...props}
            onSave={hideUploadAction ? null : () => onSave(dataItem)}
            saveComponent={hideUploadAction ? '' : 'upload'}
            onCancel={() => onCancel(dataItem)}
            dataItem={dataItem}
            editField='inEdit'
            isLoading={dataItem.isSubmitting}
            disabled={isSubmitting}
          />
        ),
      },
    ];

    if (showDriverDoc) {
      defaultColumns.splice(3, 0, driverDocColumn);
    }

    return defaultColumns;
  }

  return (
    <section className='card-fluid'>
      <legend className='card-title'>Files to upload</legend>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '.5em',
        }}
      >
        {files.length > 1 && !hideUploadAction && (
          <Button primary onClick={onSaveAll} icon={isSubmitting ? undefined : 'upload'}>
            {isSubmitting && <LoaderSpinner sise='small' type='pulsing' style={{ color: 'black' }} />}
            Upload all
          </Button>
        )}
      </div>
      <DataGrid
        hasExport={false}
        hasUnifiedTextSearch={false}
        data={files}
        exportEnabled={false}
        onItemChange={onItemChange}
        editField='inEdit'
        dataItemKey='tempId'
        columns={getColumns()}
        hideActionsCell
      />
    </section>
  );
};

export default FilesToUploadGrid;
